import {
  useMemberPaymentMethodListAsMember,
  useMutationMembershipUnlinkSecondaryAsMember,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
} from "@gymflow/api";
import { UserMemberBean } from "@gymflow/types";
import {
  Button,
  ChevronRightIcon,
  DotsVerticalIcon,
  MenuPopover,
} from "apps/portal/src/components/atoms";
import { CancelIcon } from "apps/portal/src/components/atoms/icons/CancelIcon";
import ChangeStatusIcon from "apps/portal/src/components/atoms/icons/ChangeStatusIcon";
import { CreditCard02Icon } from "apps/portal/src/components/atoms/icons/CreditCard02Icon";
import { CreditCardRefreshIcon } from "apps/portal/src/components/atoms/icons/CreditCardRefreshIcon";
import { File06Icon } from "apps/portal/src/components/atoms/icons/File06Icon";
import { LinkBroken01Icon } from "apps/portal/src/components/atoms/icons/LinkBroken01Icon";
import { ConfirmModal } from "apps/portal/src/components/templates";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import { RouteFeature } from "apps/portal/src/routes";
import useGymflowModels from "apps/portal/src/store";
import { DateTime } from "luxon";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { ApiResolverContext } from "../../../../../providers/ApiResolverProvider/ApiResolverContext";
import {
  getBillingText,
  getMembershipTypeText,
  getTillDateText,
} from "../../../utils/getText";
import { HostedPagesProfileGetMembership } from "./components/HostedPagesProfileGetMembership";
import { HostedPagesProfileMembershipStatusesHeader } from "./components/HostedPagesProfileMembershipStatusesHeader/HostedPagesProfileMembershipStatusesHeader";
import { HostedPagesProfileStatus } from "./components/HostedPagesProfileStatus";

type HostedPagesProfileMembershipStatusesProps = {
  userMember?: UserMemberBean;
  isLoadingUserMember: boolean;
};

export const HostedPagesProfileMembershipStatuses = ({
  userMember,
  isLoadingUserMember,
}: HostedPagesProfileMembershipStatusesProps) => {
  const [isOpenMenu, setIsOpenMenu] = useState(false);

  const { api } = useGymflowModels();
  const {
    club: { timezone, defaultCurrency },
    properties: { gymOpeningDate },
  } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { t } = useTranslation();
  const history = useHistory();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const { setModal, hide } = useContext(ModalContext);

  const { data: activeSubscription, isLoading } =
    useQueryMemberGetCurrentMembershipSubscriptionAsMember(
      {
        api,
      },
      { refetchOnWindowFocus: true },
    );

  const { mutateAsync: mutateAsyncMembershipUnlinkSecondary } =
    useMutationMembershipUnlinkSecondaryAsMember({
      api,
    });

  const { data: dataPaymentMethodList, isLoading: isLoadingPaymentMethodList } =
    useMemberPaymentMethodListAsMember({
      api,
      clubId,
      memberId: userMember?.id,
      enabled:
        isOpenMenu && !isLoadingUserMember && activeSubscription?.isPrimary,
    });

  const defaultPaymentMethod = dataPaymentMethodList?.find(
    (e) => e.defaultPaymentMethod,
  );

  const menuOptions = useMemo(() => {
    const baseOptions = [
      {
        id: "terms",
        label: t(
          "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.menuPopover.options.viewTerms",
        ),
        classNameLabel: "text-gray-950 dark:text-gray-25",
        icon: (
          <File06Icon
            className="size-5"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        onClick: () => {
          window.open(activeSubscription?.termsConditionsUrl, "_blank");
        },
      },
      {
        id: "paymentMethod",
        label: (
          <div className="flex w-full justify-between">
            <div className="dark:text-gray-25 text-sm font-medium text-gray-950">
              {t(
                "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.menuPopover.options.paymentMethod.label",
              )}
            </div>
            {defaultPaymentMethod?.last4Digits && (
              <div className="flex items-center gap-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t(
                    "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.menuPopover.options.paymentMethod.endingIn",
                    {
                      last4Digits: defaultPaymentMethod?.last4Digits,
                    },
                  )}
                </div>
                <ChevronRightIcon
                  className="size-5"
                  pathClassName="stroke-gray-500 dark:stroke-gray-400"
                />
              </div>
            )}
          </div>
        ),
        icon: (
          <CreditCard02Icon
            className="size-5"
            pathClassName="stroke-gray-500 dark:stroke-gray-400"
          />
        ),
        onClick: () => {
          history.push(createSiteOrEmbedLink(RouteFeature.SiteProfilePayments));
        },
      },
      {
        id: "changeMembership",
        label: t(
          "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.menuPopover.options.changeMembership",
        ),
        classNameLabel: "text-gray-950 dark:text-gray-25",
        icon: (
          <ChangeStatusIcon
            className="size-5"
            pathClassName="fill-gray-500 dark:fill-gray-400"
          />
        ),
        onClick: () => {
          history.push(
            createSiteOrEmbedLink(RouteFeature.SiteProfileChangeMembership),
          );
        },
      },
      {
        id: "cancelMembership",
        label: t(
          "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.menuPopover.options.cancelMembership",
        ),
        classNameLabel: "text-error-600 dark:text-error-400",
        icon: (
          <CancelIcon
            className="size-5"
            pathClassName="stroke-error-600 dark:stroke-error-400"
          />
        ),
        onClick: () => {
          history.push(
            createSiteOrEmbedLink(RouteFeature.SiteProfileCancelMembership),
          );
        },
        topDivider: true,
      },
      {
        id: "unlink",
        label: t(
          "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.menuPopover.options.unlink",
        ),
        classNameLabel: "text-error-600 dark:text-error-400",
        icon: (
          <LinkBroken01Icon
            className="size-5"
            pathClassName="stroke-error-600 dark:stroke-error-400"
          />
        ),
        onClick: () => {
          setModal(
            <ConfirmModal
              type="danger"
              title={t(
                "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.confirmModal.unlink.title",
              )}
              onCancel={hide}
              confirmText={t(
                "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.confirmModal.unlink.confirmText",
              )}
              onConfirm={async () => {
                if (!activeSubscription) {
                  return;
                }
                await mutateAsyncMembershipUnlinkSecondary({
                  subscriptionId: activeSubscription?.id,
                });
                hide();
              }}
              icon={<LinkBroken01Icon className="stroke-error-600" />}
              showCloseButton
            >
              {t(
                "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.confirmModal.unlink.description",
              )}
            </ConfirmModal>,
          );
        },
        topDivider: true,
      },
    ];

    return baseOptions.filter((option) => {
      if (option.id === "paymentMethod" && !activeSubscription?.isPrimary) {
        return false;
      }
      if (
        option.id === "changeMembership" &&
        !activeSubscription?.changeMembershipAvailable
      ) {
        return false;
      }
      if (
        option.id === "cancelMembership" &&
        !activeSubscription?.cancelMembershipAvailable
      ) {
        return false;
      }
      if (option.id === "unlink" && activeSubscription?.isPrimary) {
        return false;
      }
      return true;
    });
  }, [
    activeSubscription,
    createSiteOrEmbedLink,
    defaultPaymentMethod?.last4Digits,
    hide,
    history,
    mutateAsyncMembershipUnlinkSecondary,
    setModal,
    t,
  ]);

  if (isLoading) {
    return null;
  }

  if (!activeSubscription) {
    return <HostedPagesProfileGetMembership />;
  }

  const isShowSubscriptionInfo =
    activeSubscription.isPrimary &&
    DateTime.fromISO(gymOpeningDate, { zone: timezone }).startOf("day") <=
      DateTime.now().setZone(timezone).startOf("day");

  return (
    <div className="bg-gray-0 shadow-xs flex flex-col rounded-2xl border border-gray-200 dark:border-gray-600 dark:bg-gray-950">
      {activeSubscription.isPrimary && (
        <HostedPagesProfileMembershipStatusesHeader
          subscription={activeSubscription}
        />
      )}
      <div className="flex flex-col gap-2 pb-3 pl-3 pr-1.5 pt-2">
        <div className="flex items-center justify-between">
          <HostedPagesProfileStatus status={activeSubscription.status} />
          <div className="flex items-center gap-1.5">
            {!activeSubscription.isPrimary && (
              <div className="bg-warning-100 text-warning-700 h-fit rounded-full px-2 py-1 text-xs font-medium">
                {t(
                  "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.secondary",
                )}
              </div>
            )}
            <MenuPopover
              classNameContent="w-[20rem]"
              triggerButton={
                <Button
                  className="ml-1.5 size-9 min-w-9 p-0"
                  intent="transparent"
                >
                  <DotsVerticalIcon
                    className="size-5"
                    pathClassName="stroke-gray-950 dark:stroke-gray-400"
                  />
                </Button>
              }
              onOpenChange={(value) => setIsOpenMenu(value)}
              isLoading={
                (isLoadingPaymentMethodList && activeSubscription?.isPrimary) ||
                isLoadingUserMember
              }
              options={menuOptions}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1 pr-4">
          <div className="dark:text-gray-25 text-base font-bold text-gray-950">
            {activeSubscription.name}
          </div>
          {activeSubscription.scheduledInvoiceIssueDate &&
            activeSubscription.isPrimary && (
              <div className="mb-1 flex flex-row items-center gap-1.5">
                <CreditCardRefreshIcon
                  className="size-4"
                  pathClassName="dark:stroke-gray-25 stroke-gray-950"
                />
                <div className="dark:text-gray-25 text-xs font-medium text-gray-950">
                  {t(
                    "pages.hostedPagesProfile.hostedPagesProfileMembershipStatuses.scheduledInvoiceIssueDate",
                    {
                      date: DateTime.fromISO(
                        activeSubscription.scheduledInvoiceIssueDate,
                      ).toLocaleString(DateTime.DATE_MED),
                    },
                  )}
                </div>
              </div>
            )}
          {isShowSubscriptionInfo && (
            <div className="flex flex-wrap items-center gap-2 self-stretch">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {getBillingText(activeSubscription, defaultCurrency)}
              </div>
              <div className="size-1.5 rounded-full bg-gray-300 dark:bg-gray-700" />
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {getMembershipTypeText(activeSubscription)}
              </div>
              {activeSubscription.endContractDate && (
                <>
                  <div className="size-1.5 rounded-full bg-gray-300 dark:bg-gray-700" />
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {getTillDateText(activeSubscription, timezone)}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
